import {
  ApiError,
  AuthError,
  NetworkError,
  NotFoundError,
  ValidationError,
  logError,
} from '../lib/errors';
import type {
  Document,
  Notification,
  Payment,
  ServiceRequest,
  User,
  Vendor,
} from '../types/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ success: boolean; data?: T; error?: string }> {
    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
          ...options.headers,
        },
        credentials: 'include',
      }).catch((error: Error) => {
        // Network errors (connection refused, timeout, DNS failure, etc.)
        throw new NetworkError(
          'Unable to connect to server - please check your internet connection',
          error
        );
      });

      // Parse response data
      let data: unknown;
      try {
        data = await response.json();
      } catch {
        // If response isn't JSON, create generic data object
        data = { message: response.statusText };
      }

      // Handle non-OK responses with specific error types
      if (!response.ok) {
        const errorData = data as {
          error?: { message?: string; fields?: Record<string, string[]> };
          message?: string;
          fields?: Record<string, string[]>;
        };
        const message =
          errorData.error?.message ||
          errorData.message ||
          `Request failed with status ${response.status}`;

        // Authentication errors
        if (response.status === 401) {
          throw AuthError.notAuthenticated(message);
        }

        // Permission errors
        if (response.status === 403) {
          throw AuthError.noPermission(message);
        }

        // Not found errors
        if (response.status === 404) {
          throw new NotFoundError(message);
        }

        // Validation errors
        if (response.status === 422) {
          const fields = errorData.error?.fields || errorData.fields;
          if (fields) {
            throw ValidationError.fromFields(fields);
          }
          throw new ValidationError(message);
        }

        // Generic API errors with status code
        throw await ApiError.fromFetch(response, endpoint, options.method);
      }

      return data as { success: boolean; data?: T; error?: string };
    } catch (error) {
      // Log error for debugging
      logError(error, { endpoint, method: options.method });

      // Re-throw our custom errors
      if (
        error instanceof ApiError ||
        error instanceof AuthError ||
        error instanceof NetworkError ||
        error instanceof NotFoundError ||
        error instanceof ValidationError
      ) {
        throw error;
      }

      // Wrap unknown errors
      throw new ApiError(
        error instanceof Error ? error.message : 'An unknown error occurred',
        500,
        endpoint,
        options.method
      );
    }
  }

  private async getAuthToken(): Promise<string | null> {
    // Get token from Clerk
    const clerkToken = await window.Clerk?.session?.getToken();
    return clerkToken || null;
  }

  // Service Requests
  async getServiceRequests(filters?: {
    status?: string;
    category?: string;
  }): Promise<ServiceRequest[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.category) params.append('category', filters.category);
    const result = await this.request<ServiceRequest[]>(`/service-requests?${params.toString()}`);
    return result.data || [];
  }

  async getServiceRequest(id: string): Promise<ServiceRequest | null> {
    const result = await this.request<ServiceRequest>(`/service-requests/${id}`);
    return result.data || null;
  }

  async createServiceRequest(data: {
    title: string;
    description: string;
    category: ServiceRequest['category'];
    priority?: ServiceRequest['priority'];
    location?: string;
    unit?: string;
    images?: string[];
  }): Promise<ServiceRequest | null> {
    const result = await this.request<ServiceRequest>('/service-requests', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result.data || null;
  }

  async updateServiceRequest(
    id: string,
    data: Partial<ServiceRequest>
  ): Promise<ServiceRequest | null> {
    const result = await this.request<ServiceRequest>(`/service-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return result.data || null;
  }

  async deleteServiceRequest(id: string): Promise<boolean> {
    const result = await this.request(`/service-requests/${id}`, {
      method: 'DELETE',
    });
    return result.success;
  }

  // Vendors
  async getVendors(filters?: {
    category?: string;
    search?: string;
    verified?: boolean;
  }): Promise<Vendor[]> {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.verified !== undefined) params.append('verified', String(filters.verified));

    const result = await this.request<Vendor[]>(`/vendors?${params.toString()}`);
    return result.data || [];
  }

  async getVendor(id: string): Promise<Vendor | null> {
    const result = await this.request<Vendor>(`/vendors/${id}`);
    return result.data || null;
  }

  async createVendor(data: Omit<Vendor, 'id' | 'createdAt' | 'updatedAt'>): Promise<Vendor | null> {
    const result = await this.request<Vendor>('/vendors', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return result.data || null;
  }

  async updateVendor(id: string, data: Partial<Vendor>): Promise<Vendor | null> {
    const result = await this.request<Vendor>(`/vendors/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return result.data || null;
  }

  async deleteVendor(id: string): Promise<boolean> {
    const result = await this.request(`/vendors/${id}`, {
      method: 'DELETE',
    });
    return result.success;
  }

  async bulkImportVendors(
    vendors: Array<Omit<Vendor, 'id' | 'createdAt' | 'updatedAt'>>
  ): Promise<Vendor[]> {
    const result = await this.request<Vendor[]>('/vendors/bulk-import', {
      method: 'POST',
      body: JSON.stringify({ vendors }),
    });
    return result.data || [];
  }

  // User Profile
  async getCurrentUser(): Promise<User | null> {
    const result = await this.request<User>('/users/me');
    return result.data || null;
  }

  async updateUserProfile(data: {
    phone?: string;
    address?: string;
    unit?: string;
  }): Promise<User | null> {
    const result = await this.request<User>('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return result.data || null;
  }

  // Payments (placeholder)
  async getPayments(): Promise<Payment[]> {
    const result = await this.request<Payment[]>('/payments');
    return result.data || [];
  }

  // Documents (placeholder)
  async getDocuments(): Promise<Document[]> {
    const result = await this.request<Document[]>('/documents');
    return result.data || [];
  }

  // Notifications (placeholder)
  async getNotifications(): Promise<Notification[]> {
    const result = await this.request<Notification[]>('/notifications');
    return result.data || [];
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
